import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/use-auth'

/**
 * AdminRoute — gates children behind the `admin` role (Story 10.2, AC-12).
 *
 * - While loading: skeleton placeholder (no spinner per NFR-DS-2).
 * - role === 'admin': renders children.
 * - role !== 'admin': renders the "Sem acesso" page (mockup
 *   `design/_preview/users-noaccess-light.png`), NOT a blank screen.
 *
 * DEVIATION FROM AC-12 (documented in the Dev Agent Record): AC-12's text says
 * "redirect to /dashboard", but the spawn brief + the approved mockup specify a
 * dedicated "Sem acesso" page. Neither is the security boundary — the real gate
 * is the server-side `requireAdmin` on `/api/users` (AC-1). We render the
 * Sem-acesso page (more specific instruction + has a mockup) and include a
 * "Voltar ao dashboard" link to honour AC-12's intent (a path back to safety).
 *
 * UX-only: a recruiter who deep-links to /settings/users still gets 403 from the
 * API on any data call, so no admin data can leak through this component.
 */
export function AdminRoute({ children }: { children: ReactElement }): ReactElement {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8" aria-busy="true">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="mx-auto h-12 w-12 rounded-full" />
          <Skeleton className="mx-auto h-6 w-40" />
          <Skeleton className="mx-auto h-4 w-64" />
        </div>
      </div>
    )
  }

  if (user?.role !== 'admin') {
    return <NoAccess />
  }

  return children
}

/** Sem acesso — shown to non-admins who reach an admin route (AC-12, mockup). */
function NoAccess(): ReactElement {
  return (
    <div
      role="alert"
      className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center"
    >
      <div className="bg-warning-soft text-warning mb-6 flex size-14 items-center justify-center rounded-full">
        <Lock className="size-6" aria-hidden="true" />
      </div>
      <h1 className="text-ink mb-2 text-2xl font-semibold tracking-tight">Sem acesso</h1>
      <p className="text-ink-2 max-w-sm text-sm">
        Esta área é exclusiva de administradores. Se você precisa de acesso, fale com um
        administrador da Genesis.
      </p>
      {/* O `Button` e não um link pintado à mão: o primário do Lumen é gradiente
          e cápsula, como o do 404. */}
      <Button asChild className="mt-6">
        <Link to="/dashboard">Voltar ao dashboard</Link>
      </Button>
    </div>
  )
}

export default AdminRoute
